import { ContactService, HistoryService } from '@/api'
import { useContactMap } from '@/hooks'
import type { IContact, ITx } from '@/interface'
import { DP, Loading } from '@/ui'
import { handleInvite, HIDE } from '@/utils'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

type PeopleSearchProps = {
  query: string
  onSelect: (c: IContact) => void
  showRecents?: boolean
  className?: string
}

export default function PeopleSearch({ query, onSelect, showRecents = true, className }: PeopleSearchProps) {
  const q = query.trim().toLowerCase()
  const contactsQ = useQuery(['contacts'], () => ContactService.GetAll())
  const transactionsQ = useQuery(['transactions'], () => HistoryService.Get())
  const loading = contactsQ.isLoading || transactionsQ.isLoading
  const contactMap = useContactMap()

  const recents = useMemo(() => {
    if (!transactionsQ.data || !showRecents) return []
    const recent = transactionsQ.data.map((tx: ITx) => contactMap?.[tx.receiverAddress]).filter(Boolean)
    return [...new Map(recent.map((c: any) => [c?.username || c?.email, c])).values()].slice(-5)
  }, [contactMap, transactionsQ.data, showRecents])

  const filtered = useMemo(() => {
    const list: IContact[] = contactsQ.data || []
    const match = (c: IContact) => !q || c?.name?.toLowerCase().includes(q) || c?.username?.toLowerCase().includes(q) || c?.email?.toLowerCase().includes(q)

    const onCray = list.filter((c) => c?.exists && match(c))
    const invite = list.filter((c) => !c?.exists && match(c))

    // sort within groups (alpha by name)
    onCray.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
    invite.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))

    return { onCray, invite }
  }, [contactsQ.data, q])

  const Row = ({ c, showInvite }: { c: IContact; showInvite?: boolean }) => (
    <button onClick={() => (showInvite ? handleInvite() : onSelect(c))} className="w-full flex items-center justify-between gap-4 rounded-lg p-3 hover:bg-neutral-50 active:bg-neutral-100">
      <DP name={c.name || c.email} size="42px" className="text-[24px]" />
      <div className="flex-1 min-w-0 text-left">
        <div className="truncate text-[15px]">{c?.name || c?.email}</div>
        {!!c?.username && <div className="text-neutral-500 text-sm truncate">{c.username}@cray</div>}
      </div>
      {showInvite && <span className="text-neutral-500 text-sm">Invite</span>}
    </button>
  )

  if (loading) return <Loading />

  return (
    <div className={twMerge(className, 'overflow-scroll')}>
      {/* Recents */}
      <div {...HIDE(!recents.length)} className="mt-4">
        <h4 className="mb-2 font-semibold">Recents</h4>
        <div className="space-y-1">
          {recents.map((c, i) => (
            <Row key={`r-${i}`} c={c as any} />
          ))}
        </div>
      </div>

      {/* On Cray */}
      {!!filtered.onCray.length && (
        <div className="mt-5">
          <h4 className="mb-2 font-semibold">On Cray</h4>
          <div className="space-y-1">
            {filtered.onCray.map((c, i) => (
              <Row key={`on-${c.username || c.email}-${i}`} c={c} />
            ))}
          </div>
        </div>
      )}

      {/* Invite to Cray */}
      {!!filtered.invite.length && (
        <div className="mt-6">
          <h4 className="mb-2 font-semibold">Invite to Cray</h4>
          <div className="space-y-1">
            {filtered.invite.map((c, i) => (
              <Row key={`in-${c.username || c.email}-${i}`} c={c} showInvite />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!recents.length && !filtered.onCray.length && !filtered.invite.length && <div className="mt-8 text-sm text-neutral-500">No matches. Try a different name.</div>}
    </div>
  )
}
